import { Nullable } from 'types/common';
import { SearchDateRangeFilterProps } from 'types/common/searchfilter';

export enum ConfirmStatus {
  PENDING = 10,
  CHECKING = 3,
  PUBLISHED = 1,
  REJECTED = -1,
  WITHDRAW = 0,
  BLOCKED = -2,
  DELETED = 14,
  DRAFT = 2,
}

export enum ItemType {
  GARMENT = 'E6B30',
  FABRIC = '82B1A',
  TRIM = '36D98',
  AVATAR = 'C6997',
  SCENE = 'SCE00',
  EVERYWEAR = 'METAV',
}

export enum ItemTypeSubcategory {
  // -- Trim -- //
  BUTTONS = 'C5375',
  FASTENERS = '32337',
  ZIPPERS = 'BC576',
  HARDWARES = 'AC05A',
  NOTIONS = '258D3',

  // -- Avatar -- //
  MODEL = '48282',
  MOTION = 'MOTIN',
  POSE = '585BB',
  HAND_POSE = 'HNDPS',
  HAIR = '3EC8C',
  SHOES = '36620',
  GLASSES = '8B2Z5',
  EARRING = '9B1Z7',
  MANNEQUIN = '7A3D4',
}

export enum SalesType {
  PAID = 0,
  FREE = 1,
  GALLERY = 2,
}

export enum Software {
  CLO = 3,
  MD = 4,
  JINNY = 0,
  OTHERS = -99,
}

export enum Sort {
  LATEST_PUBLISHED_DATE = 0,
  OLDEST_PUBLISHED_DATE = 1,
  LATEST_UPDATE = 2,
  OLDEST_UPDATE = 3,
}

export type ConfirmItemArrayFilterValueType =
  | ConfirmStatus
  | ItemType
  | ItemTypeSubcategory
  | SalesType
  | Software
  | string;

export enum ZipStatus {
  ERROR = -1,
  COMPLETE = 1,
  UPDATING = 2,
}

export interface ConfirmItem {
  id: string;
  status: ConfirmStatus;
}

export interface CheckedItemInfo {
  id: string;
  itemTitle: string;
  requestStatus: Nullable<ConfirmStatus>;
  liveStatus: Nullable<ConfirmStatus>;
  isGarment: boolean;
}

export interface StatusSelectOption {
  label: string;
  value: string;
  isCommentRequired?: boolean;
  isReasonRequired?: boolean;
  isDisabled?: boolean;
  hideComment?: boolean;
}

export interface BaseSearchParams extends SearchDateRangeFilterProps {
  page: number;
  sort: number;
  limit: number;
}

export interface ConfirmItemSearchParams extends BaseSearchParams {
  statuses: number[];
  salesTypes: number[];
  itemTypes: string[];
  softwares: number[];
  mdPick: boolean;
  curatorsPick: boolean;
  keyword: string;
  keywordType: ConfirmItemKeywordType;
  contestFilter: string[];
  convertingStatuses: number[];
}

interface GamewearBaseFilter {
  statuses: number[];
  salesTypes: number[];
  gameTypes: string[];
  mdPick: boolean;
  curatorsPick: boolean;
  keyword: string;
  keywordType: ConfirmItemKeywordType;
  inzoiRecolor: number[];
  vrchatAvatarCompatibility: string[];
  vrchatPlatform: string[];
  vrchatItemType: string[];
}

export interface GamewearFilterForm extends GamewearBaseFilter {
  limit: number;
}

export interface GamewearSearchParams extends GamewearBaseFilter, BaseSearchParams {}

export enum GamewearFilterKey {
  GAME_TYPES = 'gameTypes',
  INZOI_RECOLOR = 'inzoiRecolor',
  VRCHAT_AVATAR_COMPATIBILITY = 'vrchatAvatarCompatibility',
  VRCHAT_PLATFORM = 'vrchatPlatform',
  VRCHAT_ITEM_TYPE = 'vrchatItemType',
}

export enum ConfirmItemTab {
  MARKETPLACE_ITEM = 'confirm-item',
  GAMEWEAR_ITEM = 'gamewear-item',
  EVERYWEAR_ITEM = 'everywear-item',
}

export enum VRChatAvatarCompatibility {
  LUCY = 'CHA01',
  OTHERS = 'CHA02',
}

export enum VRChatPlatform {
  PC = 'PLA01',
  QUEST = 'PLA02',
  PC_QUEST = 'PLA03',
}

export enum VRChatItemType {
  CHARACTER = 'IT100',
  CLOTHING = 'IT200',
  ACCESSORY = 'IT300',
  POSE_MOTION = 'IT400',
  CHARACTER_FEATURES = 'IT500',
}

export enum RecolorOption {
  AVAILABLE = 1,
  UNAVAILABLE = 0,
}

export type ConfirmItemTabType = {
  tab: ConfirmItemTab;
};

export interface ConfirmItemFilterForm {
  statuses: number[];
  itemTypes: string[];
  salesTypes: number[];
  softwares: number[];
  mdPick: boolean;
  curatorsPick: boolean;
  keyword: string;
  keywordType: ConfirmItemKeywordType;
  contestFilter: string[];
  convertingStatuses: number[];
  limit: number;
}

// [EN] Ordinals are a backend contract (sent as `keywordType`) — append only, never reorder.
// [KO] 각 값은 백엔드와의 계약이다 (`keywordType` 으로 전송) — 끝에 추가만 하고 순서를 바꾸지 말 것.
export enum ConfirmItemKeywordType {
  Account,
  CreatorName,
  ItemNumber,
  ItemId,
  ItemName,
  ItemTag,
}

export enum ConvertingFailStatus {
  ZIP_FAILED = 1,
  SREST_FAILED,
  STANDARD_FORMAT_FAILED,
}

export interface FilterForm {
  statuses: number[];
  itemTypes: string[];
  salesTypes: number[];
  softwares: number[];
  mdPick: boolean;
  curatorsPick: boolean;
  keyword: string;
  keywordType: ConfirmItemKeywordType;
  contestFilter: string[];
  convertingStatuses: number[];
}

export enum StandardFormatType {
  'FBX_THICK' = '_fbx_thick',
  'FBX_THIN' = '_fbx_thin',
  'GLTF_THICK' = '_gltf_thick',
  'GLTF_THIN' = '_gltf_thin',
}

export enum StoreType {
  CONNECT = 0, // CONNECT + MD
  GAMEWEAR = 2,
}
